
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Building2, X, Plus, Trash } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AddDivisionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (division: {
    name: string;
    description: string;
    headName: string;
    headTitle: string;
    budget: number;
    vision: string;
    mission: string;
    goals: string[];
  }) => void;
}

const AddDivisionModal = ({ isOpen, onOpenChange, onSave }: AddDivisionModalProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [headName, setHeadName] = useState("");
  const [headTitle, setHeadTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [vision, setVision] = useState("");
  const [mission, setMission] = useState("");
  const [goals, setGoals] = useState<string[]>([""]);
  
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReset = () => {
    setName("");
    setDescription("");
    setHeadName("");
    setHeadTitle("");
    setBudget("");
    setVision("");
    setMission("");
    setGoals([""]);
  };
  
  const handleAddGoal = () => {
    setGoals([...goals, ""]);
  };
  
  const handleChangeGoal = (index: number, value: string) => {
    const updatedGoals = [...goals];
    updatedGoals[index] = value;
    setGoals(updatedGoals);
  };
  
  const handleRemoveGoal = (index: number) => {
    if (goals.length === 1) return;
    const updatedGoals = [...goals];
    updatedGoals.splice(index, 1);
    setGoals(updatedGoals);
  };
  
  const handleSave = async () => {
    if (!name || !headName || !headTitle || !budget) {
      toast({
        title: "Missing required fields",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    const filteredGoals = goals.filter((goal) => goal.trim() !== "");

    if (filteredGoals.length === 0) {
      toast({
        title: "Missing goals",
        description: "Please add at least one strategic goal",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (onSave) {
        await onSave({
          name,
          description,
          headName,
          headTitle,
          budget: parseFloat(budget),
          vision,
          mission,
          goals: filteredGoals,
        });
      }

      handleReset();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving division:", error);
      toast({
        title: "Error",
        description: "Failed to save division",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleReset();
      }
      onOpenChange(open);
    }}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Add New Division
          </DialogTitle>
          <DialogDescription>
            Create a new division within your organization
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Division Name*</Label>
            <Input
              id="name"
              placeholder="e.g., Tourism Division"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Brief description of the division's responsibilities"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="headName">Division Head Name*</Label>
              <Input
                id="headName"
                placeholder="e.g., Juan Dela Cruz"
                value={headName}
                onChange={(e) => setHeadName(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="headTitle">Head Title*</Label>
              <Input
                id="headTitle"
                placeholder="e.g., Director"
                value={headTitle}
                onChange={(e) => setHeadTitle(e.target.value)}
              />
            </div>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="budget">Budget (₱)*</Label>
            <Input
              id="budget"
              type="number"
              placeholder="e.g., 1000000"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="vision">Division Vision</Label>
            <Textarea
              id="vision"
              placeholder="Enter division vision statement"
              value={vision}
              onChange={(e) => setVision(e.target.value)}
              rows={3}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="mission">Division Mission</Label>
            <Textarea
              id="mission"
              placeholder="Enter division mission statement"
              value={mission}
              onChange={(e) => setMission(e.target.value)}
              rows={3}
            />
          </div>
          
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label>Strategic Goals*</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={handleAddGoal}
                className="h-8"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Goal
              </Button>
            </div>
            
            <div className="space-y-3">
              {goals.map((goal, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder={`Strategic goal ${index + 1}`}
                    value={goal}
                    onChange={(e) => handleChangeGoal(index, e.target.value)}
                  />
                  {goals.length > 1 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleRemoveGoal(index)}
                      className="h-8 w-8 flex-shrink-0"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            <Building2 className="h-4 w-4 mr-2" />
            {isSubmitting ? "Creating..." : "Create Division"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddDivisionModal;
